// frontend/src/modules/expenses/pages/ExpensesPage.tsx
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Plus, Filter, Receipt, Wallet, CheckCircle2, AlertCircle, Download,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { useAuth } from '../../../core/auth/AuthProvider';
import { usePermission } from '../../../core/auth/usePermission';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatMoney } from '../../../design-system/format';
import { BentoGrid, GlassCard, Eyebrow, Avatar } from '../../../design-system/pds';
import { StatusBadge } from '../components/StatusBadge';
import { expensesApi } from '../api';
import type { Expense, ExpenseStatus, Category, ExpenseListParams } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

const STATUS_OPTIONS: Array<ExpenseStatus | 'all'> = ['all', 'draft', 'submitted', 'approved', 'rejected', 'reimbursed'];
const PAGE_SIZE = 50;

export function ExpensesPage() {
  const { identity } = useAuth();
  const { tenant } = useBranding();
  const tenantCurrency = (tenant as any)?.currency ?? 'USD';

  const [status, setStatus] = React.useState<ExpenseStatus | 'all'>('all');
  const [categoryId, setCategoryId] = React.useState<string>('all');
  const [employeeId, setEmployeeId] = React.useState<string>('all');
  const [from, setFrom] = React.useState<string>('');
  const [to, setTo] = React.useState<string>('');
  const [page, setPage] = React.useState(0);
  const [exporting, setExporting] = React.useState(false);

  // Filter object reused by the list fetch and the CSV export.
  const filterParams: ExpenseListParams = React.useMemo(() => ({
    status: status !== 'all' ? status : undefined,
    category_id: categoryId !== 'all' ? categoryId : undefined,
    employee_id: employeeId !== 'all' ? employeeId : undefined,
    from: from || undefined,
    to: to || undefined,
  }), [status, categoryId, employeeId, from, to]);

  // Reset to the first page whenever the filters change.
  React.useEffect(() => { setPage(0); }, [status, categoryId, employeeId, from, to]);

  const path = React.useMemo(() => {
    const qs = new URLSearchParams();
    if (status !== 'all') qs.set('status', status);
    if (categoryId !== 'all') qs.set('category_id', categoryId);
    if (employeeId !== 'all') qs.set('employee_id', employeeId);
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    qs.set('limit', String(PAGE_SIZE));
    qs.set('offset', String(page * PAGE_SIZE));
    return `/api/expenses?${qs.toString()}`;
  }, [status, categoryId, employeeId, from, to, page]);

  const { data, loading, error, reload } = useApi<{ rows: Expense[]; total: number }>(path);
  const expenses = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleExport = async () => {
    setExporting(true);
    try {
      await expensesApi.downloadCsv(filterParams);
    } catch (err: any) {
      // e.g. a trial tenant's 402 trial_export_blocked — surface the
      // backend's actual message rather than failing silently.
      toast.error('Export failed', { description: err?.message ?? String(err) });
    } finally {
      setExporting(false);
    }
  };

  const categoriesQ = useApi<{ rows: Category[] }>('/api/expenses/categories');
  // The employee filter is a MANAGER control: it only makes sense for someone
  // who can actually see other people's claims. It used to show for every tenant
  // user (`identity?.type === 'user'`), which advertised company-wide expense
  // visibility to ordinary employees. The backend now scopes reads to your own
  // claims unless you hold `expenses.read_all`, so gate the UI on the same key.
  const canReadAll = usePermission('expenses.read_all');
  const showEmployeeFilter = identity?.type === 'user' && canReadAll;
  const employeesQ = useApi<{ employees: EmployeeWithUser[] }>(showEmployeeFilter ? '/api/hr/employees' : null);

  // KPIs in tenant currency (sum amounts as-is — backend stores in tenant
  // currency by default; mixed currencies just sum numerically here).
  const sumAmt = (rows: Expense[]) => rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalAmt = sumAmt(expenses);
  const submitted = expenses.filter((e) => e.status === 'submitted');
  const approved  = expenses.filter((e) => e.status === 'approved');
  const reimbursed = expenses.filter((e) => e.status === 'reimbursed');
  const rejected   = expenses.filter((e) => e.status === 'rejected');

  const pendingAmt    = sumAmt(submitted);
  const approvedAmt   = sumAmt(approved);
  const reimbursedAmt = sumAmt(reimbursed);

  const filtersActive =
    status !== 'all' || categoryId !== 'all' || employeeId !== 'all' || from !== '' || to !== '';

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Money · Expenses"
        title="Expenses"
        description="Track team spend, approve receipts, reimburse what you owe."
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="default"
              variant="outline"
              className="gap-1.5"
              onClick={handleExport}
              disabled={exporting}
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? 'Exporting…' : 'Export to accounting'}
            </Button>
            <Link to="/dashboard/expenses/new">
              <Button size="default" className="gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                New expense
              </Button>
            </Link>
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {/* KPI bento */}
        <BentoGrid cols={12}>
          <GlassCard className="col-span-12 lg:col-span-6" padding="p-7">
            <div className="flex items-start justify-between mb-3 gap-3">
              <div className="min-w-0">
                <Eyebrow className="mb-2">Total in view</Eyebrow>
                <p className="text-stat-lg tabular-nums font-bold text-ink dark:text-white leading-none truncate">
                  {formatMoney(totalAmt, tenantCurrency)}
                </p>
                <p className="text-[13px] text-ink/55 dark:text-white/55 mt-2">
                  Across {expenses.length} expense{expenses.length === 1 ? '' : 's'}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-coral/10 flex items-center justify-center shrink-0">
                <Receipt className="h-4 w-4 text-coral-600" />
              </div>
            </div>
          </GlassCard>

          <KpiTile
            label="Pending"
            value={formatMoney(pendingAmt, tenantCurrency)}
            caption={`${submitted.length} awaiting review`}
            icon={<AlertCircle className="h-3.5 w-3.5 text-coral-600" />}
            iconBg="bg-coral/10"
          />
          <KpiTile
            label="Approved"
            value={formatMoney(approvedAmt, tenantCurrency)}
            caption={`${approved.length} cleared`}
            icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
            iconBg="bg-emerald-500/10"
          />
          <KpiTile
            label="Reimbursed"
            value={formatMoney(reimbursedAmt, tenantCurrency)}
            caption={`${reimbursed.length} paid back · ${rejected.length} rejected`}
            icon={<Wallet className="h-3.5 w-3.5 text-ink" />}
            iconBg="bg-lime-neon"
          />
        </BentoGrid>

        {/* Filters */}
        <section className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-full bg-white/65 dark:bg-white/[0.05] backdrop-blur-md shadow-pill text-[12px] font-semibold text-ink/55 dark:text-white/55">
            <Filter className="h-3 w-3" />
            Filter
          </div>
          <Select value={status} onValueChange={(v) => setStatus(v as any)}>
            <SelectTrigger className="h-10 w-auto min-w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s === 'all' ? 'All statuses' : s[0].toUpperCase() + s.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger className="h-10 w-auto min-w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {(categoriesQ.data?.rows ?? []).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showEmployeeFilter && (
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="h-10 w-auto min-w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {(employeesQ.data?.employees ?? []).map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="inline-flex items-center gap-2">
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-10 w-[150px] rounded-full bg-card/80"
              aria-label="From date"
            />
            <span className="text-[12px] text-ink/45 dark:text-white/45">to</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-10 w-[150px] rounded-full bg-card/80"
              aria-label="To date"
            />
          </div>
          {filtersActive && (
            <button
              type="button"
              onClick={() => { setStatus('all'); setCategoryId('all'); setEmployeeId('all'); setFrom(''); setTo(''); }}
              className="text-[12px] font-semibold text-ink/55 dark:text-white/55 hover:text-ink dark:hover:text-white ml-1"
            >
              Clear filters
            </button>
          )}
          <div className="ml-auto flex items-center gap-4">
            <Link
              to="/dashboard/expenses/approvals"
              className="text-[12px] font-semibold text-ink/65 dark:text-white/65 hover:text-coral-600 transition-colors"
            >
              My approvals
            </Link>
            <Link
              to="/dashboard/expenses/reimburse"
              className="text-[12px] font-semibold text-ink/65 dark:text-white/65 hover:text-coral-600 transition-colors"
            >
              Reimburse
            </Link>
            <Link
              to="/dashboard/expenses/categories"
              className="text-[12px] font-semibold text-ink/65 dark:text-white/65 hover:text-coral-600 transition-colors"
            >
              Categories →
            </Link>
          </div>
        </section>

        {/* List */}
        <GlassCard padding="p-0" className="overflow-hidden">
          {loading ? (
            <ListSkeleton />
          ) : error ? (
            <ErrorState title="Couldn't load expenses" description={error} onRetry={reload} />
          ) : expenses.length === 0 ? (
            <ExpensesEmptyState filtered={filtersActive} />
          ) : (
            <ExpensesTable expenses={expenses} tenantCurrency={tenantCurrency} />
          )}
        </GlassCard>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-[13px] text-ink/65 dark:text-white/65">
            <span className="tabular-nums">
              Showing {total === 0 ? 0 : page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                disabled={page === 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev
              </Button>
              <span className="tabular-nums px-1">Page {page + 1} of {pageCount}</span>
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                disabled={page + 1 >= pageCount || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────── inner bits ────────────────────────────── */

function KpiTile({
  label,
  value,
  caption,
  icon,
  iconBg,
}: {
  label: string;
  value: React.ReactNode;
  caption: string;
  icon: React.ReactNode;
  iconBg: string;
}) {
  return (
    <GlassCard className="col-span-12 sm:col-span-6 lg:col-span-2" padding="p-6">
      {/* The tile is only ~2/12 of the row on lg, so a long label ("Reimbursed")
       *  next to a shrink-0 icon used to push the icon clean out of the card.
       *  min-w-0 + truncate lets the label give way instead of overflowing, and
       *  the tighter tracking means it rarely has to. */}
      <div className="flex items-center justify-between mb-3 gap-2">
        <Eyebrow className="min-w-0 truncate !tracking-[0.10em]" title={label}>{label}</Eyebrow>
        <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
          {icon}
        </div>
      </div>
      <p className="text-stat-md tabular-nums font-bold text-ink dark:text-white leading-none truncate">{value}</p>
      <p className="text-[12px] text-ink/55 dark:text-white/55 mt-2">{caption}</p>
    </GlassCard>
  );
}

function ExpensesTable({
  expenses,
  tenantCurrency,
}: {
  expenses: Expense[];
  tenantCurrency: string;
}) {
  const navigate = useNavigate();
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0">
        <thead>
          <tr className="text-[10.5px] uppercase tracking-[0.14em] text-ink/55 dark:text-white/55">
            <th className="text-left py-3 px-5 font-semibold">Date</th>
            <th className="text-left py-3 px-4 font-semibold">Employee</th>
            <th className="text-left py-3 px-4 font-semibold">Category</th>
            <th className="text-left py-3 px-4 font-semibold">Description</th>
            <th className="text-right py-3 px-4 font-semibold">Amount</th>
            <th className="text-right py-3 px-5 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {expenses.map((e, i) => (
            <motion.tr
              key={e.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.015, 0.18) }}
              onClick={() => navigate(`/dashboard/expenses/${e.id}`)}
              className="cursor-pointer hover:bg-white/40 dark:hover:bg-white/[0.04] transition-colors border-t border-ink/6 dark:border-white/8"
            >
              <td className="py-3.5 px-5 align-middle text-[13px] text-ink dark:text-white tabular-nums whitespace-nowrap">
                {formatDate(e.spent_at)}
              </td>
              <td className="py-3.5 px-4 align-middle">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Avatar name={e.employee_name} size="sm" />
                  <span className="text-[13px] text-ink dark:text-white truncate max-w-[180px]">
                    {e.employee_name}
                  </span>
                </div>
              </td>
              <td className="py-3.5 px-4 align-middle text-[13px] text-ink/75 dark:text-white/75 whitespace-nowrap">
                {e.category_name}
              </td>
              <td className="py-3.5 px-4 align-middle text-[13px] text-ink/65 dark:text-white/65 max-w-[300px] truncate">
                {e.description ?? <span className="italic text-ink/40 dark:text-white/35">No description</span>}
              </td>
              <td className="py-3.5 px-4 align-middle text-right text-[14px] font-semibold tabular-nums font-mono text-ink dark:text-white whitespace-nowrap">
                {formatMoney(e.amount, e.currency || tenantCurrency)}
              </td>
              <td className="py-3.5 px-5 align-middle text-right">
                <StatusBadge status={e.status} />
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ListSkeleton() {
  return (
    <ul className="divide-y divide-ink/6 dark:divide-white/8">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="h-9 w-9 rounded-full bg-pearl-200 dark:bg-white/10 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-pearl-200 dark:bg-white/10 animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-pearl-100 dark:bg-white/5 animate-pulse" />
          </div>
          <div className="h-3 w-20 rounded bg-pearl-200 dark:bg-white/10 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}

function ExpensesEmptyState({ filtered }: { filtered: boolean }) {
  if (filtered) {
    return (
      <div className="py-16 px-8 text-center">
        <p className="text-[14px] font-semibold text-ink dark:text-white">No expenses match those filters</p>
        <p className="text-[13px] text-ink/55 dark:text-white/55 mt-1">
          Try clearing the filters above or widening the date range.
        </p>
      </div>
    );
  }
  return (
    <div className="py-16 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-16 w-16 rounded-3xl bg-coral/12 flex items-center justify-center">
        <Receipt className="h-6 w-6 text-coral-600" strokeWidth={1.6} />
      </div>
      <p className="text-[20px] font-semibold text-ink dark:text-white tracking-[-0.015em]">No expenses yet</p>
      <p className="text-[14px] text-ink/60 dark:text-white/60 mt-2 leading-relaxed">
        Log your first business expense — snap a photo of the receipt, pick a category, done.
      </p>
      <div className="mt-6 flex items-center justify-center gap-2.5 flex-wrap">
        <Link to="/dashboard/expenses/new">
          <Button className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Create expense</Button>
        </Link>
      </div>
    </div>
  );
}
